import React, { useState, useEffect } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowId,
  GridRenderCellParams
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import { Link, TextField, Chip} from "@mui/material";
import IPage from "../../../types/page.type";
import {Box, Breadcrumbs, Button, Grid, Typography} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import IAccount from "../../../types/account.type";
import {getAccounts} from "../../../services/account.service";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const AccountsList: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const [accountsPage, setAccountsPage] = useState<IPage>({ content: [], totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState<any>("asc");
  const [keywordFilter, setKeywordFilter] = useState( "");

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef<IAccount>[] = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60,
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Nombre',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'active',
      headerName: 'Activo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={`${params.row.active  ? 'success' : 'error'}`}
                     label={`${params.row.active  ? 'SI' : 'NO'}`}
                     sx={{width: 70, fontWeight: 'bold'}}/>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const handleAddClick = () => {
    navigate('create');
  };

  const handleEditClick = (id: GridRowId) => () => {
    navigate('edit/'+id)
  };

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setKeywordFilter(event.target.value)
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchAccounts(newPaginationModel.page);
  };

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    let field = sortModel[0] ? sortModel[0].field : "id";
    let order = sortModel[0] ? sortModel[0].sort : "asc";
    fetchAccounts(paginationModel.page, paginationModel.pageSize, field, order);
    setSortColumn(field);
    setSortDirection(order);
  }, []);

  const fetchAccounts = async (page: number = paginationModel.page, size: number = paginationModel.pageSize, sort: string = sortColumn, order: any = sortDirection) => {
    setLoading(true);
    type Param = {[key: string] : string}
    const params: Param = {}
    params.page = String(page);
    params.size = String(size);
    params.sort = sort;
    params.order = order;

    if (keywordFilter) {
      params.keyword = keywordFilter;
    }

    getAccounts(params).then( (response) => {
      setAccountsPage(response.data)
      setLoading(false);
    })
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <AccountBalanceWalletIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Cuentas
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => handleAddClick()}
                  sx={{ cursor: 'pointer' }}
                >
                  <AddCircleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} /> Crear Nueva
                </Link>
              </Typography>
            </Breadcrumbs>
          </Box>

          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={9} md={9} sm={9} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="keyword"
                  label="Buscar por Nombre"
                  name="keyword"
                  variant="filled"
                  type="text"
                  onChange={handleChangeKeyword}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchAccounts();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>
            <Grid xl={3} md={3} sm={3} xs={12}>
              <Button
                variant="outlined"
                fullWidth
                style={{height: 56}}
                endDecorator={<SearchIcon />}
                onClick={() => fetchAccounts()}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', height: 632, typography: 'body1' }} ml={1} mt={0} pr={2}>

            <CssVarsProvider>
              <DataGrid
                rows={accountsPage.content}
                columns={columns}
                columnVisibilityModel={{
                  id: false,
                }}
                paginationModel={paginationModel}
                rowCount={accountsPage.totalElements}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
                disableRowSelectionOnClick
              />
            </CssVarsProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountsList;
